// ============================================================
// RepeatBarlines — shared repeat marker components
// Used by both BarCell (grid mode) and SlashBar (slash mode)
// ============================================================

import React from 'react';

export function RepeatStartBarline() {
  return (
    <div
      className="absolute left-0 top-0 bottom-0 flex items-stretch pointer-events-none"
      style={{ width: 10, zIndex: 2 }}
      aria-label="Repeat start"
    >
      <div style={{ width: 3, background: 'var(--color-ink-primary)', borderRadius: 1 }} />
      <div style={{ width: 1, background: 'var(--color-ink-primary)', marginLeft: 2 }} />
      <div className="flex flex-col justify-center gap-1 ml-1">
        <div style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--color-ink-primary)' }} />
        <div style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--color-ink-primary)' }} />
      </div>
    </div>
  );
}

interface RepeatEndBarlineProps {
  count: number;
  onClick: (e: React.MouseEvent) => void;
}

export function RepeatEndBarline({ count, onClick }: RepeatEndBarlineProps) {
  return (
    <div
      className="absolute right-0 top-0 bottom-0 flex items-stretch cursor-pointer group/repeat"
      style={{ width: 22, zIndex: 2 }}
      onClick={onClick}
      title="Click to edit repeat"
      aria-label="Repeat end"
    >
      <div className="flex flex-col justify-center gap-1 mr-1">
        <div style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--color-ink-primary)' }} />
        <div style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--color-ink-primary)' }} />
      </div>
      <div style={{ width: 1, background: 'var(--color-ink-primary)' }} />
      <div style={{ width: 3, background: 'var(--color-ink-primary)', marginLeft: 2, borderRadius: 1 }} />
      <div
        className="absolute -top-1 right-0 text-[9px] font-mono text-accent bg-paper border border-accent rounded px-0.5 leading-tight opacity-0 group-hover/repeat:opacity-100 transition-opacity"
        style={{ transform: 'translateY(-100%)' }}
      >
        ×{count}
      </div>
    </div>
  );
}

export function RepeatCountBadge({ count }: { count: number }) {
  return (
    <div
      className="absolute text-[9px] font-mono text-accent leading-tight pointer-events-none"
      style={{ top: 2, right: 6 }}
    >
      ×{count}
    </div>
  );
}

// ── Repeat end popover ────────────────────────────────────

interface RepeatEndPopoverProps {
  count: number;
  onCountChange: (n: number) => void;
  onRemove: () => void;
  onClose: () => void;
}

export function RepeatEndPopover({
  count,
  onCountChange,
  onRemove,
  onClose,
}: RepeatEndPopoverProps) {
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="absolute right-0 top-full mt-1 z-30 bg-paper border border-rule rounded shadow-dropdown p-3 flex flex-col gap-2 min-w-[130px]"
    >
      <div className="text-[9px] font-label tracking-widest uppercase text-ink-muted">
        Repeat count
      </div>
      <div className="flex items-center gap-2">
        <button className="control-btn text-lg" onClick={() => onCountChange(Math.max(2, count - 1))}>−</button>
        <span className="font-mono text-sm text-ink-primary w-6 text-center">×{count}</span>
        <button className="control-btn text-lg" onClick={() => onCountChange(count + 1)}>+</button>
      </div>
      <button
        onClick={onRemove}
        className="text-[11px] text-error hover:underline text-left font-mono mt-1"
      >
        Remove repeat
      </button>
    </div>
  );
}
